/**
 * Maintenance mode in the browser: the poll, the 503 fast path, and Settings →
 * Maintenance mode's reads and writes.
 *
 * SigmaBuddy's SIGM-205 `MaintenanceContext`, adapted to TanStack Query: one
 * cached query is the context, and `useSiteMaintenanceSync` (called once, by the
 * shell) is what keeps it honest. Kept out of `queries.ts` on purpose, as
 * `receipt-reading-queries.ts` is.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  isSiteMaintenanceStatus,
  msUntilSiteMaintenance,
  nextSiteMaintenancePollMs,
  type EndSiteMaintenanceBody,
  type SetSiteMaintenanceBody,
  type SiteMaintenanceSettings,
  type SiteMaintenanceStatus,
} from "@truckbuddy/contract";
import { SITE_MAINTENANCE_EVENT, apiRequest } from "./api";

export const siteMaintenanceKeys = {
  status: ["site-maintenance", "status"] as const,
  settings: ["site-maintenance", "settings"] as const,
};

/**
 * `GET /maintenance-status`, outside `/api` like the session probe, so a plain
 * fetch rather than `apiRequest` (which prefixes `/api`).
 *
 * Throws on anything that is not a status. TanStack Query keeps the last good
 * answer through an error, which is the rule this needs: a failed status read
 * must never itself close the app.
 */
async function fetchSiteMaintenanceStatus(): Promise<SiteMaintenanceStatus> {
  const response = await fetch("/maintenance-status", {
    credentials: "same-origin",
    cache: "no-store",
  });
  if (!response.ok) throw new Error(`maintenance status answered ${response.status}`);
  const body: unknown = await response.json();
  if (!isSiteMaintenanceStatus(body)) throw new Error("maintenance status was not a status");
  return body;
}

/**
 * The status, polled. Slowly while nothing is announced (two minutes), faster
 * once a window is scheduled, every fifteen seconds while one is active so a
 * locked-out person is let back in soon after it ends, and always with jitter so
 * a building full of browsers does not poll in step.
 *
 * Paused while the tab is hidden; coming back to it asks at once.
 */
export function useSiteMaintenanceStatus() {
  return useQuery({
    queryKey: siteMaintenanceKeys.status,
    queryFn: fetchSiteMaintenanceStatus,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const data = query.state.data;
      const until = msUntilSiteMaintenance(data, Date.now(), query.state.dataUpdatedAt || Date.now());
      return nextSiteMaintenancePollMs(data?.state, until);
    },
    retry: 1,
  });
}

/**
 * Keep the status honest. Called ONCE, by the shell.
 *
 * Two jobs. A refused request (`SITE_MAINTENANCE_EVENT`, fired by `api.ts`) puts
 * its window straight into the cache, so the page changes on the failed call.
 * And when a window ends, every query is fetched again: the screen underneath
 * comes back by itself, with nothing for the person to click.
 */
export function useSiteMaintenanceSync(): void {
  const client = useQueryClient();
  const state = useSiteMaintenanceStatus().data?.state;

  useEffect(() => {
    const onRefused = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      if (isSiteMaintenanceStatus(detail)) {
        client.setQueryData(siteMaintenanceKeys.status, detail);
      }
    };
    window.addEventListener(SITE_MAINTENANCE_EVENT, onRefused);
    return () => window.removeEventListener(SITE_MAINTENANCE_EVENT, onRefused);
  }, [client]);

  const previous = useRef(state);
  useEffect(() => {
    if (previous.current === "active" && state !== undefined && state !== "active") {
      void client.invalidateQueries();
    }
    previous.current = state;
  }, [client, state]);
}

/** Ticks every `intervalMs` while `enabled`, so a countdown stays live. */
export function useNow(intervalMs: number, enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs]);
  return now;
}

/* ── the announcement popup's acknowledgements ───────────────────────── */

export const SITE_MAINTENANCE_ACK_KEY = "truckbuddy.siteMaintenanceAck";
export const SITE_MAINTENANCE_ACK_FINAL_KEY = "truckbuddy.siteMaintenanceAckFinal";

/**
 * localStorage, not sessionStorage: sessionStorage is per tab, and somebody with
 * four tabs open would be told about the same window four times. Wrapped,
 * because the accessor itself throws in a private window with storage blocked,
 * and a popup is not worth a crash. Unreadable means "not acknowledged", so the
 * person sees the notice again rather than silently missing it.
 */
export function readSiteMaintenanceAck(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeSiteMaintenanceAck(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Worst case the person is told twice, which beats a crash.
  }
}

/* ── Settings → Maintenance mode ─────────────────────────────────────── */

/** `GET /api/site-maintenance`, admin only. */
export function useSiteMaintenanceSettings() {
  return useQuery({
    queryKey: siteMaintenanceKeys.settings,
    queryFn: () => apiRequest<SiteMaintenanceSettings>("/site-maintenance"),
    staleTime: 0,
    // While a window is scheduled the screen's state line has to turn active by
    // itself at the start, so it is re-read on the same beat as the status.
    refetchInterval: 15_000,
  });
}

function useSiteMaintenanceWrite<TBody>(path: string, method: "PUT" | "POST") {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) => apiRequest<SiteMaintenanceSettings>(path, { method, body }),
    onSuccess: (saved) => {
      client.setQueryData(siteMaintenanceKeys.settings, saved);
      // The admin's own banner and popup follow at once rather than on the poll.
      client.setQueryData(siteMaintenanceKeys.status, saved.status);
    },
    onError: () => {
      // A 409 means somebody else's write is newer: show what is stored now.
      void client.invalidateQueries({ queryKey: siteMaintenanceKeys.settings });
    },
    retry: false,
  });
}

/** `PUT /api/site-maintenance`: arm, edit, or start now. */
export function useSetSiteMaintenance() {
  return useSiteMaintenanceWrite<SetSiteMaintenanceBody>("/site-maintenance", "PUT");
}

/** `POST /api/site-maintenance/end`. */
export function useEndSiteMaintenance() {
  return useSiteMaintenanceWrite<EndSiteMaintenanceBody>("/site-maintenance/end", "POST");
}
